import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PossibleDuplicate } from '@nemo/api';
import { useTRPC } from '../trpc.js';
import { useToast } from '../ui/Toast.js';
import { CollapsibleCard } from './CollapsibleCard.js';
import { invalidateFundRows } from '../grid/invalidateFundRows.js';

/**
 * Two rows that may be one firm, which no rule can settle: a fund beside its
 * own sub-brand ("KKR" / "KKR Growth") or a name with no website to check.
 * Merging folds one into the other by the merge tool's rules; keeping both is
 * remembered, so the pair is not asked about again.
 */
export function PossibleDuplicates(): React.JSX.Element {
  const trpc = useTRPC();
  const toast = useToast();
  const queryClient = useQueryClient();

  const pairs = useQuery(trpc.duplicates.list.queryOptions());

  const settled = (message: string): void => {
    void queryClient.invalidateQueries({ queryKey: trpc.duplicates.list.queryKey() });
    invalidateFundRows(queryClient, trpc);
    void queryClient.invalidateQueries({ queryKey: trpc.enrichment.status.queryKey() });
    toast(message);
  };
  const onError = (error: { message: string }): void => toast(error.message);

  const mergeFunds = useMutation(
    trpc.duplicates.mergeFunds.mutationOptions({
      onSuccess: (merged) => settled(`Merged into ${merged.into}.`),
      onError,
    }),
  );
  const mergeCandidate = useMutation(
    trpc.duplicates.mergeCandidate.mutationOptions({
      onSuccess: () => settled('The discovered fund joined the existing fund.'),
      onError,
    }),
  );
  const keepBoth = useMutation(
    trpc.duplicates.keepBoth.mutationOptions({
      onSuccess: () => settled('Kept both.'),
      onError,
    }),
  );
  const busy = mergeFunds.isPending || mergeCandidate.isPending || keepBoth.isPending;

  const list = pairs.data ?? [];

  return (
    <CollapsibleCard id="duplicates" title="Possible duplicates" count={list.length}>
      <p className="subtle">{waitingPhrase(list.length)}</p>
      {list.map((pair) => (
        <div className="review-row" key={keyOf(pair)}>
          {pair.kind === 'funds' ? (
            <>
              <p className="review-said">
                <strong>{pair.from.name}</strong> and <strong>{pair.into.name}</strong>
              </p>
              <p className="subtle">{pair.reason}</p>
              <div className="review-actions">
                <button
                  className="button small"
                  type="button"
                  disabled={busy}
                  onClick={() => mergeFunds.mutate({ fromId: pair.from.id, intoId: pair.into.id })}
                >
                  Merge into {pair.into.name}
                </button>
                <button
                  className="button small"
                  type="button"
                  disabled={busy}
                  onClick={() => mergeFunds.mutate({ fromId: pair.into.id, intoId: pair.from.id })}
                >
                  Merge into {pair.from.name}
                </button>
                <button
                  className="button small"
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    keepBoth.mutate({ fundId: pair.into.id, other: { fundId: pair.from.id } })
                  }
                >
                  Keep both
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="review-said">
                <strong>{pair.candidate.name}</strong> (seen in {pair.candidate.roundsSeen}{' '}
                {pair.candidate.roundsSeen === 1 ? 'round' : 'rounds'}, Discovered) and{' '}
                <strong>{pair.fund.name}</strong>
              </p>
              <p className="subtle">{pair.reason}</p>
              <div className="review-actions">
                <button
                  className="button small"
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    mergeCandidate.mutate({ candidateId: pair.candidate.id, fundId: pair.fund.id })
                  }
                >
                  Merge into {pair.fund.name}
                </button>
                <button
                  className="button small"
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    keepBoth.mutate({
                      fundId: pair.fund.id,
                      other: { candidateId: pair.candidate.id },
                    })
                  }
                >
                  Keep both
                </button>
              </div>
            </>
          )}
        </div>
      ))}
    </CollapsibleCard>
  );
}

function keyOf(pair: PossibleDuplicate): string {
  return pair.kind === 'funds'
    ? `funds:${pair.from.id}:${pair.into.id}`
    : `candidate:${pair.candidate.id}:${pair.fund.id}`;
}

function waitingPhrase(count: number): string {
  if (count === 0) return 'nothing is waiting for review';
  return count === 1 ? '1 pair waiting for review' : `${count} pairs waiting for review`;
}
